import angular from 'angular';

import '../../vendor/ng-appcache/src/appcache';

import apiServiceModule from './APIService';
import analyticsServiceModule from './AnalyticsService';
import env from '../env';
import { getTimeDiffString, friendlyUserAgentString, compareVersions } from '../util';

import UAParser from 'user-agent-parser';

const appVersionPath = '/app_version';
const androidPackageDownloadPath = '/download_apk/staffing_app/latest';
const androidDownloadPageSubhost = 'staff';
const androidDownloadPageAbsolutePath = '/android';
const IOSDownloadPageAbsolutePath = '/ios';
const IOSDownloadPageSubhost = 'staff';
const appNameTranslationKey = 'STAFF_APP';

class AppVersionService {
  constructor(
    $window,
    $q,
    $translate,
    $timeout,
    appcache,
    APIService,
    APP_CONFIG,
    LogService,
    AnalyticsService
  ) {
    this.$window = $window;
    this.$q = $q;
    this.$translate = $translate;
    this.$timeout = $timeout;
    this.appcache = appcache;
    this.APIService = APIService;
    this.APP_CONFIG = APP_CONFIG;
    this.LogService = LogService;
    this.AnalyticsService = AnalyticsService;

    this.init();
  }

  init() {
    this._appName = '';
    this.$translate(appNameTranslationKey)
      .then(appName => this._appName = appName);

    this._fullUserAgent = this.$window && this.$window.navigator && this.$window.navigator.userAgent || '';
    this._friendlyUserAgent = friendlyUserAgentString(this._fullUserAgent);

    this._uaParser = new UAParser(this._fullUserAgent);
    this._uaParserOS = this._uaParser && this._uaParser.getOS();
    this._uaParserDevice = this._uaParser && this._uaParser.getDevice();

    this.AnalyticsService.setCustomAttribute('PACKAGE_VERSION', this.packageVersion);
    this.AnalyticsService.setCustomAttribute('GIT_VERSION', this.gitVersion);
    this.AnalyticsService.setCustomAttribute('CORDOVA_APP_VERSION', this.cordovaAppVersion);
    this.AnalyticsService.setCustomAttribute('BUILD_TARGET', this.buildTarget);
    this.AnalyticsService.setCustomAttribute('RUNNING_ON', this.runningOn);
  }

  get shortAppVersionInfo() {
    return `${this.appName} ${this.appVersion}`;
  }

  translateShortAppVersionInfo() {
    return this.translateAppName()
      .then(appName => `${appName} ${this.appVersion}`);
  }

  get extendedAppVersionInfo() {
    return `${this.packageName} ${this.packageVersion} tag ${this.gitVersion} cordova app ${this.cordovaAppVersion}, '${this.buildTarget}' target built at ${this.buildDate} running on ${this.runningOn}`;
  }

  get extendedAppVersionInfoWithAge() {
    return `${this.packageName} ${this.packageVersion} tag ${this.gitVersion} cordova app ${this.cordovaAppVersion}, '${this.buildTarget}' target built at ${this.buildDate} (${this.buildAge}) running on ${this.runningOn}`;
  }

  get fullAppVersionInfo() {
    return `${this.shortAppVersionInfo} (${this.extendedAppVersionInfo})`;
  }

  get fullAppVersionInfoWithAge() {
    return `${this.shortAppVersionInfo} (${this.extendedAppVersionInfoWithAge})`;
  }

  get appName() {
    return this._appName;
  }

  get appNameTranslationKey() {
    return appNameTranslationKey;
  }

  get device() {
    return this.$window && this.$window.device;
  }

  get devicePlatform() {
    return this.device && this.device.platform;
  }

  translateAppName() {
    return this.$translate(appNameTranslationKey);
  }

  get appVersion() {
    return this.packageVersion;
  }

  get packageName() {
    return env.PACKAGE_NAME;
  }

  get packageVersion() {
    return env.PACKAGE_VERSION;
  }

  get gitVersion() {
    return env.GIT_VERSION;
  }

  get isAndroid() {
    if (!this._uaParserOS) {
      this.LogService && this.LogService.warn(`No user agent OS available for isAndroid`);
      return false;
    }
    return this._uaParserOS.name === 'Android';
  }

  get isIOS() {
    if (!this._uaParserOS) {
      this.LogService && this.LogService.warn(`No user agent OS available for isIOS`);
      return false;
    }
    return this._uaParserOS.name === 'iOS';
  }

  get isWindows() {
    if (!this._uaParserOS) {
      this.LogService && this.LogService.warn(`No user agent OS available for isWindows`);
      return false;
    }
    switch (this._uaParserOS.name) {
      case 'Windows':
      case 'Windows Phone':
      case 'Windows Mobile':
      case 'Windows [Phone/Mobile]':
        return true;
      default:
        return false;
    }
  }

  get isCordova() {
    return !!(this.$window && (this.$window.cordova || this.$window.PhoneGap));
  }

  get isAndroidCordova() {
    return this.isCordova && this.isAndroid;
  }

  get isIOSCordova() {
    return this.isCordova && this.isIOS;
  }

  get isWindowsCordova() {
    return this.isCordova && this.isWindows;
  }

  get cordovaAppVersion() {
    return env.CORDOVA_APP_VERSION;
  }

  get cordovaVersion() {
    return this.isCordova && this.$window.cordova && this.$window.cordova.version;
  }

  get buildDate() {
    return env.buildDate;
  }

  get buildAge() {
    return getTimeDiffString(this.buildDate);
  }

  get loadedAge() {
    return getTimeDiffString(env.loadedAtDate);
  }

  get runningOn() {
    let info = this.userAgent;
    if (this.isCordova) {
      info += ' with Cordova ' + this.cordovaVersion;
    }
    return info;
  }

  get userAgent() {
    return this._friendlyUserAgent;
  }

  get buildTarget() {
    return env.BUILD_TARGET;
  }

  versionCheck() {
    if (this.isCordova) {
      return this.cordovaVersionCheck();
    } else {
      return this.webVersionCheck();
    }
  }

  cordovaVersionCheck() {
    if (!this.APIService.isOnline) {
      this.LogService.warn("Version check requested but app is offline");
      return this.$q.when(undefined);
    }
    return this.getLatestAppVersionInfo()
      .then(versionInfo => {
        const compareResult = compareVersions(this.cordovaAppVersion, versionInfo.CORDOVA_APP_VERSION);
        if (compareResult === -1) {
          this.LogService.log("Found new Cordova version");
          this.AnalyticsService.trackEvent('VERSION_CHECK_CORDOVA_FOUND_NEW_VERSION', {
            current_cordova_version: this.cordovaAppVersion,
            server_cordova_version: versionInfo.CORDOVA_APP_VERSION,
            current_git_version: this.gitVersion,
            server_git_version: versionInfo.GIT_VERSION,
            current_package_version: this.packageVersion,
            server_package_version: versionInfo.PACKAGE_VERSION,
          });
          return true;
        } else {
          this.LogService.log("No new Cordova version");
          return false;
        }
      });
  }

  webVersionCheck() {
    if (!this.APIService.isOnline) {
      this.LogService.warn("Version check requested but app is offline");
      return this.$q.when(undefined);
    }
    return this.getLatestAppVersionInfo()
      .then(versionInfo => {
        const gitCompareResult = compareVersions(this.gitVersion, versionInfo.GIT_VERSION);
        const packageCompareResult = compareVersions(this.packageVersion, versionInfo.PACKAGE_VERSION);
        if (gitCompareResult === -1 || packageCompareResult === -1) {
          this.AnalyticsService.trackEvent('VERSION_CHECK_WEB_FOUND_NEW_VERSION', {
            current_git_version: this.gitVersion,
            server_git_version: versionInfo.GIT_VERSION,
            current_package_version: this.packageVersion,
            server_package_version: versionInfo.PACKAGE_VERSION,
          });
          this.LogService.log("webVersionCheck() returning true because we found new version");
          return true;
        } else {
          this.LogService.log("webVersionCheck() returning false because no new version");
          return false;
        }
      });
  }

  update() {
    if (typeof device !== 'undefined' && device) {
      if (device.platform === 'Android') {
        return this.cordovaUpdate();
      }
    } else {
      return this.webUpdate();
    }
  }

  showIOSUpdate() {
    return this._show
  }

  cordovaUpdate() {
    return this._cordovaDownloadAndUpdate();
  }

  webUpdate() {
    return this.webUpdateAppcacheAndForceReload();
  }

  versionCheckAndUpdate() {
    if (this.isCordova) {
      return this.cordovaVersionCheckAndUpdate();
    } else {
      return this.webVersionCheckAndUpdate();
    }
  }

  cordovaVersionCheckAndUpdate() {
    return this.cordovaVersionCheck()
      .then(versionCheckResult => {
        if (versionCheckResult) {
          // New version available!
          return this._cordovaDownloadAndUpdate();
        }
      });
  }

  webVersionCheckAndUpdate() {
    return this.webVersionCheck()
      .then(newVersionAvailable => {
        if (newVersionAvailable) {
          return this.webUpdate();
        } else {
          return false;
        }
      });
  }

  webUpdateAppcacheAndForceReload() {
    const appCacheStatus = this.appcache.status;
    const appCacheStatusUncached = 0;

    this.LogService.debug(`AppVersionService.webUpdateAppcacheAndForceReload - Checking for AppCache update. Current appcache status:`, this.appcache.status, 'textstatus:', this.appcache.textStatus);
    if (appCacheStatus === appCacheStatusUncached) {
      // App cache not enabled; go ahead with reload
      this.LogService.debug(`AppVersionService.webUpdateAppcacheAndForceReload - App cache not enabled; reloading`);
      return this.webForceReload();
    } else {
      return this.appcache.checkUpdate()
        .catch(err => {
          this.LogService.warn(`AppVersionService.webUpdateAppcacheAndForceReload - no update available! Skipping.`);
          return this.$q.reject(err);
        })
        .then(() => {
          this.LogService.debug(`AppVersionService.webUpdateAppcacheAndForceReload - update available! Reloading...`);
          return this.webForceReload();
        });
    }
  }

  webForceReload() {
    this.AnalyticsService.trackEvent('VERSION_UPDATE_WEB');
    const delay = 1000;
    return this.$timeout(() => {
      return this.$window.location.reload(true);
    }, delay);
  }

  getLatestAppVersionInfo() {
    const config = { force: true };
    return this.APIService.get(appVersionPath, config)
      .then(response => {
        return response.data;
      });
  }

  openAndroidDownloadPage() {
    const url = this.APIService.convertAbsolutePathToURLWithHostPrefix(androidDownloadPageAbsolutePath, androidDownloadPageSubhost);
    this.LogService.info("Opening URL:", url);
    return this.$window.open(url, '_blank');
  }

  openIOSDownloadPage() {
    const url = this.APIService.convertAbsolutePathToURLWithHostPrefix(IOSDownloadPageAbsolutePath, IOSDownloadPageSubhost);
    return this.$window.open(url, '_blank');
  }

  /**
   * Download latest Cordova APK to persistent storage and then update
   */
  _cordovaDownloadAndUpdate() {
    this.AnalyticsService.trackEvent('VERSION_UPDATE_CORDOVA');

    const permissionsPlugin = this.$window
      && this.$window.cordova
      && this.$window.cordova.plugins
      && this.$window.cordova.plugins.permissions
      || null;
    if (!permissionsPlugin) {
      return this.$q.reject("Cordova Permissions plugin not found");
    }

    /**
     * Check an individual permission; resolve with status
     */
    const checkPermission = perm => this.$q((resolve, reject) => {
      const callback = status => {
        this.LogService.log(`checkPermission(${perm}) hasPermission=${status.hasPermission}`);
        resolve(status);
      };
      permissionsPlugin.hasPermission(perm, callback, null);
    });

    /**
     * Request a list of permissions
     */
    const requestPermissions = permList => this.$q((resolve, reject) => {
      const success = status => {
        if (status.hasPermission) {
          resolve(status);
        } else {
          reject(status);
        }
      };
      const error = err => {
        reject(err);
      };
      permissionsPlugin.requestPermissions(permList, success, error);
    });

    /**
     * Check, and, if necessary, request, a list of permissions
     */
    const checkAndRequestPermissions = permissionsList => {
      return this.$q
        .all(permissionsList.map(perm => checkPermission(perm)))
        .then(statusList => {
          if (!statusList.every(status => status.hasPermission)) {
            return requestPermissions(permissionsList);
          }
          return true;
        });
    };

    const url = this.APIService.convertPathToURL(androidPackageDownloadPath);
    const FileTransfer = this.$window.FileTransfer;
    if (!FileTransfer) {
      return this.$q.reject("Cordova FileTransfer plugin not found");
    }
    const transfer = new FileTransfer();
    const dest = [this.$window.cordova.file.externalApplicationStorageDirectory, 'staff-app-install.apk'].join('/');
    const trustAllHosts = false;

    let requestedPermissions = [
      permissionsPlugin.READ_EXTERNAL_STORAGE,
      permissionsPlugin.WRITE_EXTERNAL_STORAGE
    ];

    return checkAndRequestPermissions(requestedPermissions)
      .then(() => {
        // Got permissions; now download file
        return this
          .$q((resolve, reject) => {
            const success = (...args) => {
              resolve.apply(this, args);
            };
            const error = (...args) => {
              reject.apply(this, args);
            };
            transfer.download(url, dest, success, error, trustAllHosts);
          })
          .then(result => {
            this.LogService.log("cordova download succeeded; result should be a FileEntry object: " + JSON.stringify(result));
            return this._cordovaLaunchUpdate(result);
          })
          .catch(err => {
            this.LogService.error("Error downloading new Cordova app: " + JSON.stringify(err));
            return this.$q.reject(err);
          })
      });
  }

  /**
   * Launch Android update for given file entry pointing to APK file
   */
  _cordovaLaunchUpdate(cordovaFileEntry) {
    const fileOpenerPlugin = this.$window
      && this.$window.cordova
      && this.$window.cordova.plugins
      && this.$window.cordova.plugins.fileOpener2
      || null;
    if (!fileOpenerPlugin) {
      this.LogService.warn(`Cordova file opener plugin not found`);
      return this.$q.reject("Cordova file opener plugin not found");
    }

    const numSuccessfulTries = 1;
    const delayBetweenTriesInMS = 10 * 1000;

    const doLaunch = () => {
      return this.$q((resolve, reject) => {
        fileOpenerPlugin.open(
          cordovaFileEntry.toURL(),
          'application/vnd.android.package-archive',
          {
            success: resolve,
            error: reject,
          },
        );
      })
        .then(result => {
          return result;
        })
        .catch(err => {
          this.LogService.warn(`Error launching intent:`, err);
          return this.$q.reject(err);
        });
    };

    /**
     * Attempt to launch the file; if we get a successful response, wait a few seconds,
     * and then re-attempt. If we get an error, return immediately.
     */
    const attemptToLaunchMultipleTimes = numTries => {
      return doLaunch()
        .then(result => {
          const numTriesRemaining = numTries - 1;
          if (numTriesRemaining > 0) {
            // Recurse
            this.LogService.debug(`AppVersionService _cordovaLaunchUpdate attemptToLaunchMultipleTimes trying again after ${delayBetweenTriesInMS}`)
            return this.$timeout(() => attemptToLaunchMultipleTimes(numTriesRemaining), delayBetweenTriesInMS);
          } else {
            // Return result
            return result;
          }
        })
        .catch(err => {
          this.LogService.debug(`AppVersionService _cordovaLaunchUpdate attemptToLaunchMultipleTimes got error:`, err);
          return this.$q.reject(err);
        })
    };
    return attemptToLaunchMultipleTimes(numSuccessfulTries);
  }
}

const moduleName = 'recaps.services.AppVersionService';
let module = angular.module(moduleName, [
  apiServiceModule.name,
  analyticsServiceModule.name,
  'ng-appcache',
]);

module.service('AppVersionService', AppVersionService);

export default module;
